import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Scale } from 'lucide-react';
import { PADDLES, METRICS, TECH_IMG } from '../data/technicals';

const BALL_IMG = `${process.env.REACT_APP_BACKEND_URL}/api/static/brand/pickleball.png?v=3`;
const RED = PADDLES.hexcore.accent;
const BLUE = PADDLES.profoam.accent;
const KEYS = ['weight', 'swing', 'twist', 'bending', 'sound'];
const SHORT = { weight: 'Weight', swing: 'Swing weight', twist: 'Twist weight', bending: 'Face bend', sound: 'Impact sound' };
const LIVE = KEYS.map((k) => METRICS.find((m) => m.key === k));
const mono = { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' };

const fade = (delay = 0) => ({
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-10% 0px' },
  transition: { duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] },
});

// Digits tumble through random glyphs for a moment, then settle on the measured value
const useTumble = (value) => {
  const [shown, setShown] = useState(value);
  useEffect(() => {
    let n = 0;
    const id = setInterval(() => {
      n += 1;
      if (n > 7) { setShown(value); clearInterval(id); return; }
      setShown(value.replace(/[0-9]/g, () => String(Math.floor(Math.random() * 10))));
    }, 45);
    return () => clearInterval(id);
  }, [value]);
  return shown;
};

const Readout = ({ k, value, unit, note, max }) => {
  const p = PADDLES[k];
  const shown = useTumble(String(value));
  return (
    <div data-testid={`teaser-readout-${k}`}>
      <p className="font-display text-sm sm:text-base uppercase tracking-tight" style={{ color: p.accent }}>{p.name}</p>
      <p className="mt-1 text-4xl sm:text-5xl lg:text-6xl text-white leading-none tabular-nums" style={mono}>
        {shown}<span className="ml-2 text-sm sm:text-base text-zinc-500 align-baseline">{unit}</span>
      </p>
      <div className="mt-3 h-1.5 w-full rounded-full bg-white/[0.07] overflow-hidden">
        <div className="h-full rounded-full transition-[width] duration-700 ease-out" style={{ width: `${(value / max) * 100}%`, background: p.accent }} />
      </div>
      <p className="mt-2 text-[11px] sm:text-xs text-zinc-400">{note}</p>
    </div>
  );
};

// Seamless scrolling dual waveform: HexCore (deeper, 785 Hz) vs ProFoam (brighter, 908 Hz)
const wave = (W, H, cycles, amp, phase) => {
  const pts = [];
  for (let x = 0; x <= W * 2; x += 6) {
    const t = (x / W) * Math.PI * 2;
    const y = H / 2 + Math.sin(t * cycles + phase) * amp * (0.65 + 0.35 * Math.sin(t * 2 + phase));
    pts.push(`${x},${y.toFixed(1)}`);
  }
  return `M${pts.join(' L')}`;
};
const W = 900, H = 120;
const HEX_PATH = wave(W, H, 7, 34, 0.4);
const PRO_PATH = wave(W, H, 9, 26, 2.1);
const lbl = { fontSize: 11, letterSpacing: 2, textTransform: 'uppercase', ...mono };

const SoundViz = () => (
  <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="absolute inset-0 w-full h-full">
    <line x1="0" x2={W} y1={H / 2} y2={H / 2} stroke="rgba(255,255,255,0.08)" />
    <g className="tt-wave" style={{ animationDuration: '14s' }}><path d={HEX_PATH} fill="none" stroke={RED} strokeWidth="2" strokeOpacity="0.9" /></g>
    <g className="tt-wave" style={{ animationDuration: '11s' }}><path d={PRO_PATH} fill="none" stroke={BLUE} strokeWidth="2" strokeOpacity="0.9" /></g>
  </svg>
);

// Mini visuals share a 600×120 box; everything sits inside x 110–490 so the mobile crop keeps it whole
const V = { w: 600, h: 120 };
const Box = ({ children }) => (
  <svg viewBox={`0 0 ${V.w} ${V.h}`} preserveAspectRatio="xMidYMid slice" className="absolute inset-0 w-full h-full">{children}</svg>
);
const small = { fontSize: 9.5, letterSpacing: 1.2, textTransform: 'uppercase', ...mono };

// Where the grams live — body / edge guard / handle, widths to scale
const MASS = { hexcore: [165.4, 37.9, 28.4], profoam: [155.0, 38.0, 27.3] };
const WeightViz = () => (
  <Box>
    {['hexcore', 'profoam'].map((k, row) => {
      const y = row ? 66 : 28; let x = 110; const scale = 380 / 240; const total = METRICS.find((m) => m.key === 'weight')[k];
      const fills = [PADDLES[k].accent, 'rgba(255,255,255,0.35)', 'rgba(255,255,255,0.18)'];
      const segs = MASS[k].map((g, i) => { const w = g * scale; const r = <rect key={i} x={x} y={y} width={w - 3} height="22" rx="3" fill={fills[i]} />; x += w; return r; });
      return (
        <g key={k}>
          <g className="tt-grow" style={{ animationDelay: `${row * 0.15}s`, transformOrigin: '110px 0' }}>{segs}</g>
          <text x="118" y={y + 15} fill="#fff" style={{ ...small, fontWeight: 700 }}>{PADDLES[k].name}</text>
          <text x={x - 8} y={y + 15} textAnchor="end" fill="#fff" style={{ fontSize: 12, ...mono }}>{total} g</text>
        </g>
      );
    })}
    <g style={{ fontSize: 9, ...mono }} fill="rgba(255,255,255,0.45)">
      <text x="110" y="110">■ BODY · FACES + CORE</text><text x="265" y="110">■ EDGE GUARD</text><text x="375" y="110">■ HANDLE & GRIP</text>
    </g>
  </Box>
);

// Pendulum: lower swing weight comes around quicker (illustrative timing)
const SW = METRICS.find((m) => m.key === 'swing');
const SwingViz = () => (
  <Box>
    {[{ k: 'hexcore', cx: 165, dur: '1.45s', note: 'CARRIES MORE' }, { k: 'profoam', cx: 345, dur: '1.15s', note: 'QUICKER' }].map(({ k, cx, dur, note }) => (
      <g key={k}>
        <line x1={cx - 55} x2={cx + 55} y1="96" y2="96" stroke="rgba(255,255,255,0.15)" />
        <g className="tt-pend" style={{ transformOrigin: `${cx}px 96px`, transformBox: 'view-box', animationDuration: dur }}>
          <image href={k === 'hexcore' ? TECH_IMG.paddleHex : TECH_IMG.paddlePro} x={cx - 34} y="32" width="68" height="68"
            style={{ filter: `drop-shadow(0 0 6px ${PADDLES[k].accent}66)` }} data-testid={`swing-paddle-${k}`} />
        </g>
        <circle cx={cx} cy="96" r="3.5" fill="#fff" />
        <text x={cx + 64} y="46" fill={PADDLES[k].accent} style={small}>{PADDLES[k].name}</text>
        <text x={cx + 64} y="68" fill="#fff" style={{ fontSize: 16, ...mono }}>{SW[k]}</text>
        <text x={cx + 64} y="86" fill="rgba(255,255,255,0.5)" style={{ fontSize: 9, ...mono }}>{note}</text>
      </g>
    ))}
  </Box>
);

// Off-centre hit seen from above: higher twist weight turns less (illustrative angles)
const TW = METRICS.find((m) => m.key === 'twist');
const TwistViz = () => (
  <Box>
    {[{ k: 'hexcore', cx: 200, amp: '5deg', note: 'TWISTS LESS' }, { k: 'profoam', cx: 400, amp: '10deg', note: 'TWISTS MORE' }].map(({ k, cx, amp, note }) => (
      <g key={k}>
        <g className="tt-twist" style={{ transformOrigin: `${cx}px 62px`, transformBox: 'view-box', '--amp': amp }}>
          <rect x={cx - 80} y="55" width="160" height="14" rx="7" fill={PADDLES[k].accent} />
          <image href={BALL_IMG} x={cx + 39} y="30" width="22" height="22" data-testid={`twist-ball-${k}`} />
        </g>
        <circle cx={cx} cy="62" r="4" fill="#fff" />
        <text x={cx - 80} y="100" fill={PADDLES[k].accent} style={small}>{PADDLES[k].name} · {TW[k]}</text>
        <text x={cx + 80} y="100" textAnchor="end" fill="rgba(255,255,255,0.5)" style={{ fontSize: 9, ...mono }}>{note}</text>
      </g>
    ))}
  </Box>
);

// Face bend vs load: HexCore even, ProFoam gives on soft shots then firms up
const BendViz = () => (
  <Box>
    <path d="M110 14 V100 H490" fill="none" stroke="rgba(255,255,255,0.2)" />
    {[1, 3, 5, 10].map((kg) => <text key={kg} x={110 + (kg / 10) * 380} y="114" textAnchor="middle" fill="rgba(255,255,255,0.45)" style={{ fontSize: 9, ...mono }}>{kg} kg</text>)}
    <path d="M110 100 L480 34" fill="none" stroke={RED} strokeWidth="2.5" strokeLinecap="round" pathLength="1" className="tt-draw" />
    <path d="M110 100 C 200 48, 330 32, 480 26" fill="none" stroke={BLUE} strokeWidth="2.5" strokeLinecap="round" pathLength="1" className="tt-draw" style={{ animationDelay: '0.2s' }} />
    <text x="300" y="92" fill={RED} style={small}>HexCore · bends evenly</text>
    <text x="160" y="30" fill={BLUE} style={small}>ProFoam · gives on soft shots</text>
  </Box>
);

const VIZ = {
  weight: { C: WeightViz, legend: 'Where the grams live', note: 'Same guard, same handle — the core decides' },
  swing: { C: SwingViz, legend: 'Effort to swing', note: 'Illustration · lower swing weight = quicker' },
  twist: { C: TwistViz, legend: 'Off-centre hit, from above', note: 'Illustration · higher twist weight = steadier' },
  bending: { C: BendViz, legend: 'Face bend vs load', note: 'Lower bend = firmer feel' },
  sound: { C: SoundViz, legend: '785 Hz vs 908 Hz', note: 'Impact sound · tone, not loudness' },
};

const Scope = ({ metric }) => {
  const { C, legend, note } = VIZ[metric];
  return (
    <div className="relative h-24 sm:h-28 overflow-hidden rounded-xl border border-white/[0.08] bg-black/40" data-testid="teaser-scope" data-viz={metric}>
      <div className="absolute inset-0 tt-grid opacity-60" />
      <div key={metric} className="absolute inset-0 tt-fade"><C /></div>
      <div className="tt-sweep absolute inset-y-0 w-px bg-white/70 shadow-[0_0_18px_4px_rgba(255,255,255,0.35)]" />
      <div className="absolute left-3 top-2 flex items-center gap-3 font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-400">
        <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ background: RED }} /><span className="w-2 h-2 rounded-full -ml-2.5" style={{ background: BLUE }} />{legend}</span>
      </div>
      <span className="absolute right-3 top-2 hidden sm:block font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-500">{note}</span>
    </div>
  );
};

const Panel = () => {
  const [i, setI] = useState(0);
  const paused = useRef(false);
  useEffect(() => {
    const id = setInterval(() => { if (!paused.current) setI((v) => (v + 1) % LIVE.length); }, 2800);
    return () => clearInterval(id);
  }, []);
  const m = LIVE[i];
  return (
    <div
      className="relative rounded-3xl border border-black/10 bg-[#0F0F12] p-5 sm:p-7 shadow-[0_40px_90px_rgba(0,0,0,0.35)] text-white"
      onMouseEnter={() => { paused.current = true; }}
      onMouseLeave={() => { paused.current = false; }}
      data-testid="compare-panel"
    >
      <div className="absolute -inset-px rounded-3xl pointer-events-none bg-[linear-gradient(135deg,rgba(224,33,40,0.25),transparent_35%,transparent_65%,rgba(30,111,217,0.25))] opacity-70" />
      <div className="relative">
        <div className="flex items-center justify-between gap-3">
          <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.28em] text-zinc-400">
            <span className="relative flex w-2 h-2"><span className="absolute inset-0 rounded-full bg-emerald-400 animate-ping opacity-60" /><span className="relative w-2 h-2 rounded-full bg-emerald-400" /></span>
            Measured data · live readout
          </p>
          <p className="hidden sm:block font-mono text-[10px] uppercase tracking-[0.28em] text-zinc-600">DYSSC-M04-2026</p>
        </div>

        <div className="mt-4 flex flex-wrap gap-1.5" data-testid="teaser-metric-chips">
          {LIVE.map((x, idx) => (
            <button
              key={x.key}
              onClick={() => setI(idx)}
              className={`rounded-full px-3 py-1 text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.15em] transition-colors ${idx === i ? 'bg-white text-black' : 'bg-white/[0.06] text-zinc-400 hover:text-white'}`}
              data-testid={`teaser-chip-${x.key}`}
            >
              {SHORT[x.key]}
            </button>
          ))}
        </div>

        <div className="mt-6 flex items-baseline justify-between gap-4">
          <h3 className="font-heading font-bold text-lg sm:text-xl text-white" data-testid="teaser-metric-label">{m.label}</h3>
          {m.condition && <p className="hidden sm:block text-xs text-zinc-500 text-right">{m.condition}</p>}
        </div>

        <div className="mt-4 grid grid-cols-2 gap-5 sm:gap-8">
          <Readout k="hexcore" value={m.hexcore} unit={m.unit} note={m.hexNote} max={m.max} />
          <Readout k="profoam" value={m.profoam} unit={m.unit} note={m.proNote} max={m.max} />
        </div>

        <div className="mt-6"><Scope metric={m.key} /></div>
      </div>
    </div>
  );
};


// Homepage band: live HexCore vs ProFoam readout with metric-driven graphs, linking to /compare
export const CompareTeaser = () => (
  <section id="compare" className="relative overflow-hidden bg-[#F6F6F3] text-[#0B0B0C] border-b border-black/[0.06]" data-testid="compare-teaser">
    <div className="absolute inset-0 opacity-[0.07] bg-[linear-gradient(rgba(0,0,0,0.5)_1px,transparent_1px),linear-gradient(90deg,rgba(0,0,0,0.5)_1px,transparent_1px)] bg-[size:48px_48px]" />
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_40%_60%_at_8%_40%,rgba(224,33,40,0.10),transparent_60%),radial-gradient(ellipse_40%_60%_at_92%_60%,rgba(30,111,217,0.12),transparent_60%)]" />
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20 grid lg:grid-cols-12 gap-10 lg:gap-8 items-center">
      <div className="lg:col-span-4">
        <motion.p {...fade(0)} className="font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.32em] font-bold text-zinc-500">Compare</motion.p>
        <motion.h2 {...fade(0.1)} className="mt-4 font-display text-4xl sm:text-5xl uppercase leading-[0.92] tracking-tight text-[#0B0B0C]" data-testid="compare-teaser-heading">
          HexCore<br /><span className="text-zinc-400">vs</span> ProFoam.
        </motion.h2>
        <motion.p {...fade(0.2)} className="mt-5 text-zinc-600 text-sm sm:text-base leading-relaxed max-w-sm">
          Firm and steady, or light and quick. Tap a measure to see how the two paddles differ — then compare them line by line.
        </motion.p>
        <motion.div {...fade(0.3)} className="mt-7">
          <Link
            to="/compare"
            className="group inline-flex items-center gap-3 rounded-full bg-[#0B0B0C] text-white font-bold uppercase tracking-[0.18em] text-sm pl-2 pr-6 py-2 shadow-[0_18px_50px_rgba(0,0,0,0.22)] hover:scale-[1.04] active:scale-[0.97] transition-transform"
            data-testid="compare-teaser-cta"
          >
            <span className="w-10 h-10 rounded-full bg-white text-black flex items-center justify-center"><Scale className="w-4 h-4" /></span>
            Compare paddles
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </motion.div>
      </div>
      <motion.div {...fade(0.15)} className="lg:col-span-8"><Panel /></motion.div>
    </div>
  </section>
);
